import {
  addDays,
  addWeeks,
  format,
  isSameMonth,
  startOfWeek,
  subWeeks,
} from 'date-fns'
import { bool, func, instanceOf, number, object, oneOf } from 'prop-types'
import { memo, useCallback } from 'react'
import { ControlBar, DateNavigator } from '@opallabs/calendar'
import { kuiperClassNames, Select } from '@opallabs/kuiper'
import FilterButton from '../../Header/FilterButton'
import SettingsMenu from '../SettingsMenu'
import { WeekViewSortType } from '../types'
import { useWeekViewSortOptions } from '../useWeekViewSortOptions'
import { useWeekViewSettings } from '../WeekViewSettingsContext'
import './styles.scss'
import { ViewModeToggle } from './ViewModeToggle'

const defaultFocusedDay = new Date()

/** "February 18-24", or "Feb 26 – Mar 4" where the week crosses a month. */
const weekLabel = (start, end) =>
  isSameMonth(start, end)
    ? `${format(start, 'MMMM d')}-${format(end, 'd')}`
    : `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`

const WeekHeader = ({
  activeFiltersCount,
  focusedDay = defaultFocusedDay,
  isFiltersTrayOpen,
  onToggleFilters,
  onWeekChange,
  story,
  theme = 'light',
  weekStartsOn,
}) => {
  const { coverImageDisplay, selectedSort, setSelectedSort } =
    useWeekViewSettings()
  const { colorByOptions, isLoadingLabelSets, sortOptions } =
    useWeekViewSortOptions()

  // The control names what the week is grouped by. Grouped by nothing there is
  // no name to give, so it falls back to the placeholder — a sort with no title
  // of its own is what draws it.
  const groupedBy =
    selectedSort?.type === WeekViewSortType.None
      ? { ...selectedSort, title: '' }
      : selectedSort

  const weekStart = startOfWeek(focusedDay, { weekStartsOn })

  const menuTrackingCallback = useCallback(
    (optionField, trackFn) => {
      const storyParams = { inStory: Boolean(story), storyId: story?.id }

      if (
        optionField === 'content-expansion' &&
        typeof trackFn === 'function'
      ) {
        trackFn('click', storyParams)
      }

      if (optionField === 'image-display' && typeof trackFn === 'function') {
        trackFn('click', {
          ...storyParams,
          action: `Card Scale: ${coverImageDisplay}`,
        })
      }
    },
    [coverImageDisplay, story]
  )

  return (
    <ControlBar
      start={
        <>
          <FilterButton
            activeFiltersCount={activeFiltersCount}
            isFiltersTrayOpen={isFiltersTrayOpen}
            onToggleFilters={onToggleFilters}
          />
          <div className={kuiperClassNames('sortSelector')}>
            <Select
              ariaLabel="Group the week by"
              // Empty draws no trailing arrows: the design keeps the control
              // to its glyph and its label.
              arrowIcon=""
              disabled={isLoadingLabelSets}
              icon="gem-rows-stacked"
              iconOptions={{ height: 16, system: 'gem', width: 16 }}
              placeholder="Group"
              value={groupedBy}
              options={sortOptions}
              onSelect={setSelectedSort}
              theme="invisible"
              flip={false}
              // What a sort is, not what it is called: the chosen one carries
              // no title while the week is grouped by nothing, and two label
              // sets can share a name.
              selectedComparator={(option, value) =>
                option.type === value.type &&
                String(option.id) === String(value.id)
              }
            />
          </div>
        </>
      }
      center={
        <DateNavigator
          label={weekLabel(weekStart, addDays(weekStart, 6))}
          nextLabel="Go to next week"
          onDateSelect={onWeekChange}
          onNext={() => onWeekChange(addWeeks(focusedDay, 1))}
          onPrevious={() => onWeekChange(subWeeks(focusedDay, 1))}
          onToday={() => onWeekChange(new Date())}
          previousLabel="Go to previous week"
          selectedDate={weekStart}
          weekStartsOn={weekStartsOn}
        />
      }
      end={
        <div
          className={kuiperClassNames('expandCardsButton', 'hasBgColor', 'n7')}
        >
          <ViewModeToggle />
          <SettingsMenu
            colorByOptions={colorByOptions}
            menuTrackingCallback={menuTrackingCallback}
            {...{
              theme,
            }}
          />
        </div>
      }
    />
  )
}

WeekHeader.propTypes = {
  activeFiltersCount: number,
  focusedDay: instanceOf(Date),
  isFiltersTrayOpen: bool,
  onToggleFilters: func.isRequired,
  onWeekChange: func,
  story: object,
  theme: oneOf(['dark', 'light']),
  weekStartsOn: oneOf([0, 1, 2, 3, 4, 5, 6]),
}

export default memo(WeekHeader)
